import { useState } from 'react';
import { Save, Mail, Bell, Target, BarChart3, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/auth-store';
import { authApi } from '../../../lib/api';

interface MKTConfig {
  emailMarketing: {
    enabled: boolean;
    senderEmail: string;
    senderName: string;
    dailyLimit: number;
  };
  notifications: {
    emailNotifications: boolean;
    smsNotifications: boolean;
    pushNotifications: boolean;
  };
  analytics: {
    googleAnalytics: boolean;
    facebookPixel: boolean;
    trackingCode: string;
  };
  campaigns: {
    autoCampaigns: boolean;
    campaignFrequency: 'daily' | 'weekly' | 'monthly';
    maxCampaignsPerDay: number;
  };
}

export default function MKTConfigurationScreen() {
  const navigate = useNavigate();
  const { user, token, setUser } = useAuthStore();
  const [config, setConfig] = useState<MKTConfig>({
    emailMarketing: {
      enabled: true,
      senderEmail: 'marketing@logishaus.com',
      senderName: 'LogisHaus Marketing',
      dailyLimit: 1000
    },
    notifications: {
      emailNotifications: true,
      smsNotifications: false,
      pushNotifications: true
    },
    analytics: {
      googleAnalytics: true,
      facebookPixel: false,
      trackingCode: 'GA-123456789'
    },
    campaigns: {
      autoCampaigns: true,
      campaignFrequency: 'weekly',
      maxCampaignsPerDay: 5
    }
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isTogglingTwoFactor, setIsTogglingTwoFactor] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSaving(false);
    // Here you would typically save to API
  };

  const handleManageTwoFactor = async () => {
    if (!isTwoFactorEnabled) {
      navigate('/settings/2fa');
      return;
    }

    if (!token || !user) {
      return;
    }

    try {
      setIsTogglingTwoFactor(true);
      const response = await authApi.disableTwoFactor(token);

      if (response.success) {
        setUser({ ...user, google_auth_enabled: false });
      }
    } finally {
      setIsTogglingTwoFactor(false);
    }
  };

  const isTwoFactorEnabled = !!user?.google_auth_enabled;

  const fieldLabelClass = 'mb-1 block text-[12px] font-medium text-[#5E6784]';
  const fieldInputClass =
    'h-9 w-full rounded-lg border border-[#D6DDEE] bg-white px-3 text-[13px] text-[#34415F] focus:outline-none focus:ring-1 focus:ring-[#BCC8E5]';
  const toggleClass =
    "relative h-5 w-9 rounded-full bg-[#D7DDE8] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-[#D3D9E5] after:bg-white after:transition-all peer-checked:bg-[#4F77FF] peer-checked:after:translate-x-4 peer-checked:after:border-white";

  return (
    <div className="mx-8 space-y-4 rounded-[12px] border border-[#E6EBF5] bg-white p-4 shadow-[0_2px_8px_rgba(25,42,78,0.05)]">
      <div className="flex justify-between items-center">
        <h1 className="text-[20px] font-semibold text-[#1E2330]">MKT Configuration</h1>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex h-9 items-center gap-2 rounded-lg border border-[#F6C600] px-3 text-[13px] font-medium text-[#CFA300] transition-colors hover:bg-[#FFF9E1] disabled:opacity-50"
        >
          <Save className="h-3.5 w-3.5" />
          {isSaving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Email Marketing Configuration */}
        <div className="rounded-[10px] border border-[#E6EBF5] bg-[#FCFDFF] p-4">
          <div className="mb-3 flex items-center">
            <Mail className="mr-2 h-5 w-5 text-blue-500" />
            <h3 className="text-[16px] font-semibold text-[#1E2330]">Email Marketing</h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-medium text-[#5E6784]">Enable Email Marketing</span>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={config.emailMarketing.enabled}
                  onChange={(e) => setConfig({
                    ...config,
                    emailMarketing: { ...config.emailMarketing, enabled: e.target.checked }
                  })}
                  className="sr-only peer"
                />
                <div className={toggleClass}></div>
              </label>
            </div>

            <div>
              <label className={fieldLabelClass}>Sender Email</label>
              <input
                type="email"
                value={config.emailMarketing.senderEmail}
                onChange={(e) => setConfig({
                  ...config,
                  emailMarketing: { ...config.emailMarketing, senderEmail: e.target.value }
                })}
                className={fieldInputClass}
              />
            </div>

            <div>
              <label className={fieldLabelClass}>Sender Name</label>
              <input
                type="text"
                value={config.emailMarketing.senderName}
                onChange={(e) => setConfig({
                  ...config,
                  emailMarketing: { ...config.emailMarketing, senderName: e.target.value }
                })}
                className={fieldInputClass}
              />
            </div>

            <div>
              <label className={fieldLabelClass}>Daily Email Limit</label>
              <input
                type="number"
                value={config.emailMarketing.dailyLimit}
                onChange={(e) => setConfig({
                  ...config,
                  emailMarketing: { ...config.emailMarketing, dailyLimit: Number(e.target.value) || 0 }
                })}
                className={fieldInputClass}
              />
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="rounded-[10px] border border-[#E6EBF5] bg-[#FCFDFF] p-4">
          <div className="mb-3 flex items-center">
            <Bell className="mr-2 h-5 w-5 text-green-500" />
            <h3 className="text-[16px] font-semibold text-[#1E2330]">Notification Settings</h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-medium text-[#5E6784]">Email Notifications</span>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={config.notifications.emailNotifications}
                  onChange={(e) => setConfig({
                    ...config,
                    notifications: { ...config.notifications, emailNotifications: e.target.checked }
                  })}
                  className="sr-only peer"
                />
                <div className={toggleClass}></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-[13px] font-medium text-[#5E6784]">SMS Notifications</span>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={config.notifications.smsNotifications}
                  onChange={(e) => setConfig({
                    ...config,
                    notifications: { ...config.notifications, smsNotifications: e.target.checked }
                  })}
                  className="sr-only peer"
                />
                <div className={toggleClass}></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-[13px] font-medium text-[#5E6784]">Push Notifications</span>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={config.notifications.pushNotifications}
                  onChange={(e) => setConfig({
                    ...config,
                    notifications: { ...config.notifications, pushNotifications: e.target.checked }
                  })}
                  className="sr-only peer"
                />
                <div className={toggleClass}></div>
              </label>
            </div>
          </div>
        </div>

        {/* Analytics Configuration */}
        <div className="rounded-[10px] border border-[#E6EBF5] bg-[#FCFDFF] p-4">
          <div className="mb-3 flex items-center">
            <BarChart3 className="mr-2 h-5 w-5 text-purple-500" />
            <h3 className="text-[16px] font-semibold text-[#1E2330]">Analytics</h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-medium text-[#5E6784]">Google Analytics</span>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={config.analytics.googleAnalytics}
                  onChange={(e) => setConfig({
                    ...config,
                    analytics: { ...config.analytics, googleAnalytics: e.target.checked }
                  })}
                  className="sr-only peer"
                />
                <div className={toggleClass}></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-[13px] font-medium text-[#5E6784]">Facebook Pixel</span>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={config.analytics.facebookPixel}
                  onChange={(e) => setConfig({
                    ...config,
                    analytics: { ...config.analytics, facebookPixel: e.target.checked }
                  })}
                  className="sr-only peer"
                />
                <div className={toggleClass}></div>
              </label>
            </div>

            <div>
              <label className={fieldLabelClass}>Tracking Code</label>
              <input
                type="text"
                value={config.analytics.trackingCode}
                onChange={(e) => setConfig({
                  ...config,
                  analytics: { ...config.analytics, trackingCode: e.target.value }
                })}
                className={fieldInputClass}
                placeholder="GA-XXXXXXXXX"
              />
            </div>
          </div>
        </div>

        {/* Campaign Settings */}
        <div className="rounded-[10px] border border-[#E6EBF5] bg-[#FCFDFF] p-4">
          <div className="mb-3 flex items-center">
            <Target className="mr-2 h-5 w-5 text-orange-500" />
            <h3 className="text-[16px] font-semibold text-[#1E2330]">Campaign Settings</h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-medium text-[#5E6784]">Auto Campaigns</span>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={config.campaigns.autoCampaigns}
                  onChange={(e) => setConfig({
                    ...config,
                    campaigns: { ...config.campaigns, autoCampaigns: e.target.checked }
                  })}
                  className="sr-only peer"
                />
                <div className={toggleClass}></div>
              </label>
            </div>

            <div>
              <label className={fieldLabelClass}>Campaign Frequency</label>
              <select
                value={config.campaigns.campaignFrequency}
                onChange={(e) => setConfig({
                  ...config,
                  campaigns: { ...config.campaigns, campaignFrequency: e.target.value as 'daily' | 'weekly' | 'monthly' }
                })}
                className={fieldInputClass}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            <div>
              <label className={fieldLabelClass}>Max Campaigns Per Day</label>
              <input
                type="number"
                value={config.campaigns.maxCampaignsPerDay}
                onChange={(e) => setConfig({
                  ...config,
                  campaigns: { ...config.campaigns, maxCampaignsPerDay: Number(e.target.value) || 0 }
                })}
                className={fieldInputClass}
              />
            </div>
          </div>
        </div>

        {/* Security / Two-Factor Authentication */}
        <div className="rounded-[10px] border border-[#E6EBF5] bg-[#FCFDFF] p-4">
          <div className="mb-3 flex items-center">
            <ShieldCheck className="mr-2 h-5 w-5 text-indigo-500" />
            <h3 className="text-[16px] font-semibold text-[#1E2330]">Security</h3>
          </div>

          <div className="space-y-2.5">
            <p className="text-[13px] text-[#5E6784]">
              Protect your account by enabling two-factor authentication (2FA) for this user.
            </p>
            <p className="text-[12px] font-medium text-[#5E6784]">
              Status:{' '}
              <span className={isTwoFactorEnabled ? 'text-green-600' : 'text-red-500'}>
                {isTwoFactorEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </p>

            <button
              onClick={handleManageTwoFactor}
              disabled={isTogglingTwoFactor}
              className="mt-1 inline-flex h-9 items-center gap-2 rounded-lg border border-[#F6C600] bg-[#FFF7D6] px-3 text-[13px] font-medium text-[#9E7E00] transition-colors hover:bg-[#FFEEAA]"
            >
              {isTogglingTwoFactor
                ? 'Updating...'
                : isTwoFactorEnabled
                  ? 'Disable 2FA'
                  : 'Enable 2FA'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
